import { useMemo, useState } from 'react';
import {
  AccordionDetails,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import { StyledButton } from 'components/Buttons';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import { Invoice, InvoiceLine } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { ImZoomIn } from 'react-icons/im';
import { MdDelete } from 'react-icons/md';
import {
  canEditInvoice,
  hasUnknownActivity,
  isForeignInvoice,
} from 'utils/invoiceFunctions.utils';
import {
  formatNetUnitPrice,
  numberWithCommas,
} from 'utils/numberFunctions.utils';
import * as S from '../../../../details.styled';
import * as D from '../../InvoiceLinesPanel.css';
import { AddInvoiceLineDialog } from '../AddInvoiceLineDialog';
import { DeleteInvoiceDialog } from '../DeleteInvoiceLineDialog';
import { InvoiceLineDetailsDialog } from '../InvoiceLineDetailsDialog';
import { InvoiceLinesSummary } from '../InvoiceLinesSummary';
import { invoiceLinesHeadCells } from './InvoiceLines.const';
import { InvoiceLinesTableData } from './InvoiceLines.types';

type Props = {
  invoice: Invoice;
  refetchInvoiceData: FetchInvoiceData;
};

function createTableData(
  index: number,
  id: number,
  code: string,
  quantity: number,
  unitCode: string,
  currencyAmount: number,
  currency: string,
  exchangeRate: number,
  netValue: number,
  taxRate: number,
  taxValue: number,
  grossValue: number,
  marked: boolean,
): InvoiceLinesTableData {
  return {
    index,
    id,
    code,
    quantity,
    unitCode,
    currencyAmount: formatNetUnitPrice(currencyAmount),
    currency,
    exchangeRate: exchangeRate.toFixed(4),
    netValue: numberWithCommas(netValue),
    taxRate: taxRate + '%',
    taxValue: numberWithCommas(taxValue),
    grossValue: numberWithCommas(grossValue),
    marked,
  };
}

export const InvoiceLines = ({ invoice, refetchInvoiceData }: Props) => {
  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
  ]);

  const { user, allowedReplacementsByCode } = useAuth();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDeleteInvoiceDialogOpen, setIsDeleteInvoiceDialogOpen] =
    useState(false);
  const [selectedLine, setSelectedLine] = useState<InvoiceLine | null>(null);

  const rows = useMemo(
    () =>
      invoice?.invoiceLines.map((line) =>
        createTableData(
          line.ordinalNumber,
          line.id as number,
          line.activity.code,
          line.quantity,
          line.unit.code,
          line.currencyAmount.value,
          line.currencyAmount.currencyCode.currency,
          line.exchangeRate,
          line.amounts.netValue,
          line.amounts.taxRate.value,
          line.amounts.taxValue,
          line.amounts.grossValue,
          hasUnknownActivity(invoice, line.activity),
        ),
      ),
    [invoice],
  );

  const canAddInvoiceLines = useMemo(
    () =>
      !invoice.publicationDate &&
      canEditInvoice(invoice, user, allowedReplacementsByCode),
    [invoice, user, allowedReplacementsByCode],
  );

  const handleOpenLineDetails = (id: number) => {
    setSelectedLine(
      invoice?.invoiceLines.find((line) => line.id === id) || null,
    );
    setIsDetailsDialogOpen(true);
  };

  const handleCloseLineDetails = () => {
    setIsDetailsDialogOpen(false);
    setSelectedLine(null);
  };

  const handleOpenDeleteLineDialog = (id: number) => {
    setSelectedLine(
      invoice?.invoiceLines.find((line) => line.id === id) || null,
    );
    setIsDeleteInvoiceDialogOpen(true);
  };

  const handleCloseDeleteLineDialog = () => {
    setIsDeleteInvoiceDialogOpen(false);
    setSelectedLine(null);
  };

  return (
    <>
      <AddInvoiceLineDialog
        open={isAddDialogOpen}
        invoice={invoice}
        onClose={() => setIsAddDialogOpen(false)}
        refetchInvoiceData={refetchInvoiceData}
      />
      <InvoiceLineDetailsDialog
        open={isDetailsDialogOpen}
        invoice={invoice}
        invoiceLine={selectedLine}
        onClose={handleCloseLineDetails}
        refetchInvoiceData={refetchInvoiceData}
      />
      <DeleteInvoiceDialog
        open={isDeleteInvoiceDialogOpen}
        invoice={invoice}
        isCorrective={false}
        invoiceLine={selectedLine}
        refetchInvoiceData={refetchInvoiceData}
        onClose={handleCloseDeleteLineDialog}
      />
      <AccordionDetails sx={{ overflowX: 'auto' }}>
        <S.Table>
          <TableHead>
            <TableRow>
              {invoiceLinesHeadCells.map((cell) => (
                <StyledTableHeadCell
                  key={cell.id}
                  align={cell.align || 'left'}
                  padding={'normal'}
                  height={'condensed'}
                >
                  {cell.label}
                </StyledTableHeadCell>
              ))}
              <StyledTableHeadCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <D.LinesTableRow
                key={row.id}
                hover
                $marked={row.marked}
                title={
                  row.marked ? t('unknownInvoiceMsg', { ns: 'invoice' }) : ''
                }
              >
                {invoiceLinesHeadCells.map((cell) => (
                  <StyledTableCell
                    key={cell.id}
                    align={cell.align || 'left'}
                    height='condensed'
                    onClick={() => handleOpenLineDetails(row.id)}
                  >
                    {row[cell.id]}
                  </StyledTableCell>
                ))}
                {!invoice.deleted && (
                  <StyledTableCell height='condensed' width='8%'>
                    <D.StyledIconButton
                      size='small'
                      color='inherit'
                      title={t('button.details', { ns: 'misc' })}
                      onClick={() => handleOpenLineDetails(row.id)}
                    >
                      <ImZoomIn size={16} />
                    </D.StyledIconButton>
                    {!invoice.publicationDate &&
                      canEditInvoice(
                        invoice,
                        user,
                        allowedReplacementsByCode,
                      ) &&
                      !invoice.exportInformation &&
                      !(
                        isForeignInvoice(invoice.invoiceClass) && !invoice.owner
                      ) && (
                        <D.StyledIconButton
                          size='small'
                          color='inherit'
                          title={t('button.delete', { ns: 'misc' })}
                          onClick={() => handleOpenDeleteLineDialog(row.id)}
                        >
                          <MdDelete size={20} />
                        </D.StyledIconButton>
                      )}
                  </StyledTableCell>
                )}
              </D.LinesTableRow>
            ))}
            <S.TableRow selected divider>
              <StyledTableCell height='condensed' colSpan={6} />
              <StyledTableCell height='condensed' align='right'>
                {t('total', { ns: 'invoice' })}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.netValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                -
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.taxValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' align='right'>
                {numberWithCommas(invoice.totals.grossValue)}
              </StyledTableCell>
              <StyledTableCell height='condensed' />
            </S.TableRow>

            <InvoiceLinesSummary taxTotals={invoice.taxTotals} />
          </TableBody>
        </S.Table>
        {canAddInvoiceLines &&
          !(isForeignInvoice(invoice.invoiceClass) && !invoice.owner) && (
            <D.AddLineContainer>
              <StyledButton
                $primary
                disabled={
                  invoice.deleted ||
                  !canEditInvoice(invoice, user, allowedReplacementsByCode)
                }
                onClick={() => setIsAddDialogOpen(true)}
              >
                {t('button.addItem', { ns: 'misc' })}
              </StyledButton>
            </D.AddLineContainer>
          )}
      </AccordionDetails>
    </>
  );
};
